import { useQuery } from '@tanstack/react-query';
import { Camera, ChevronLeft, ChevronRight, Loader2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { Attachment } from '../lib/types';
import { ImageHoverInfo } from './Hover';

/**
 * A file size somebody would say out loud.
 *
 * `Math.round(bytes / 1024)` printed "0 KB" under a real photograph, which
 * reads as a broken upload rather than a small thumbnail. Bytes below a
 * kilobyte, kilobytes below a megabyte, one decimal above.
 */
const fileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * The photos a technician took, where the people who need them can see them.
 *
 * Evidence was write-only for most of this project's life: technicians
 * documented every job and nobody could open the result. A dispatcher settling
 * "was the splice actually done" had to ring the technician and ask.
 *
 * `urlFor` is passed in because the two callers authenticate differently — the
 * office pages ride the session cookie, the field app has only its link token —
 * and neither should have to know about the other.
 */
export function PhotoGallery({
  attachments,
  urlFor,
  emptyHint = 'No photos yet.',
}: {
  attachments: Attachment[];
  urlFor: (attachmentId: string) => string;
  emptyHint?: string;
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const step = useCallback(
    (delta: number) =>
      setOpenIndex((current) => {
        if (current === null) return current;
        // Wraps, so a long press on the arrow does not dead-end.
        return (current + delta + attachments.length) % attachments.length;
      }),
    [attachments.length],
  );

  // The lightbox is a keyboard trap otherwise: Escape is how people expect to
  // leave a full-screen image, and arrows are how they expect to move.
  useEffect(() => {
    if (openIndex === null) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenIndex(null);
      if (event.key === 'ArrowRight') step(1);
      if (event.key === 'ArrowLeft') step(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openIndex, step]);

  if (attachments.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyHint}</p>;
  }

  const open = openIndex === null ? null : attachments[openIndex];

  return (
    <>
      <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {attachments.map((attachment, index) => (
          <li key={attachment.id}>
            {/*
              Who took it and when, over the photo, on hover.

              This is the one place in the console where the image *is* the
              record, and those facts had nowhere to sit: they were in the
              lightbox, which you have to open one photo at a time. A dispatcher
              checking whether the splice photos are all from the same visit had
              to open six of them. Now they read the grid.
            */}
            <ImageHoverInfo
              src={urlFor(attachment.id)}
              alt={attachment.caption ?? `${attachment.kind} photo`}
              title={attachment.caption ?? attachment.kind}
              facts={[
                attachment.uploadedBy ?? 'Uploader not recorded',
                new Date(attachment.createdAt).toLocaleString(),
                fileSize(attachment.sizeBytes),
              ]}
              onOpen={() => setOpenIndex(index)}
            />
            {/* The same caption below, unchanged. On a phone there is no hover,
                so the overlay above is an accelerator and this is the record. */}
            <p className="flex items-center justify-between gap-2 px-1 py-1 text-[11px] text-muted-foreground">
              <span className="truncate">{attachment.caption ?? attachment.kind}</span>
              <span className="shrink-0 tabular-nums">
                {new Date(attachment.createdAt).toLocaleDateString()}
              </span>
            </p>
          </li>
        ))}
      </ul>

      {open && (
        <div
          className="fixed inset-0 z-50 flex flex-col bg-black/90 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Photo viewer"
        >
          <div className="flex items-start justify-between gap-4 text-white">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{open.caption ?? open.kind}</p>
              <p className="text-xs text-white/70">
                {open.uploadedBy ? `${open.uploadedBy} · ` : ''}
                {new Date(open.createdAt).toLocaleString()} ·{' '}
                {fileSize(open.sizeBytes)}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpenIndex(null)}
              className="rounded-lg p-1.5 hover:bg-white/10"
              aria-label="Close"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          <div className="flex min-h-0 flex-1 items-center gap-2">
            {attachments.length > 1 && (
              <button
                type="button"
                onClick={() => step(-1)}
                className="rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
                aria-label="Previous photo"
              >
                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
              </button>
            )}

            <img
              src={urlFor(open.id)}
              alt={open.caption ?? `${open.kind} photo`}
              className="mx-auto max-h-full min-h-0 flex-1 object-contain"
            />

            {attachments.length > 1 && (
              <button
                type="button"
                onClick={() => step(1)}
                className="rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
                aria-label="Next photo"
              >
                <ChevronRight className="h-5 w-5" aria-hidden="true" />
              </button>
            )}
          </div>

          {attachments.length > 1 && (
            <p className="pt-2 text-center text-xs text-white/70 tabular-nums">
              {(openIndex ?? 0) + 1} of {attachments.length}
            </p>
          )}
        </div>
      )}
    </>
  );
}

/** The office version: fetches the list itself and rides the session cookie. */
export function TicketPhotos({ ticketId }: { ticketId: string }) {
  const photos = useQuery({
    queryKey: ['ticket-attachments', ticketId],
    queryFn: () => api.ticketAttachments(ticketId),
  });

  return (
    <section className="card">
      <h3 className="mb-3 flex items-center gap-1.5 font-heading text-lg">
        <Camera className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        Photos
        {photos.data && photos.data.attachments.length > 0 && (
          <span className="text-sm font-normal text-muted-foreground">
            ({photos.data.attachments.length})
          </span>
        )}
      </h3>

      {photos.isPending && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}

      {photos.data && (
        <PhotoGallery
          attachments={photos.data.attachments}
          urlFor={(attachmentId) => api.attachmentUrl(ticketId, attachmentId)}
          emptyHint="Nothing photographed on this job yet."
        />
      )}
    </section>
  );
}
